import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { Card } from '@/components/UI';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import { trpc, trpcClient } from '@/api/trpc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntryScorecardsRow } from '@/types/appRouter';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import ResourceAccessNotice from '@/components/resourceAccess/ResourceAccessNotice';
import ShareResourceModal from '@/components/resourceAccess/ShareResourceModal';
import AddRuleCard from './components/AddRuleCard';
import BaselineExperimentCard from './components/BaselineExperimentCard';
import LinkedExperimentCard from './components/LinkedExperimentCard';
import PreviewCard from './components/PreviewCard';
import RulesCard from './components/RulesCard';
import ScorecardMetadataCard from './components/ScorecardMetadataCard';
import { defaultThresholds } from './components/constants';
import { buildStrategyContextQueryString, parseStrategyContextFromQuery } from './components/helpers';
import type { ScorecardRuleRow, SizingBand, ThresholdsMeta } from './components/types';

const ScorecardEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const scorecardId = Number(id);
    const queryClient = useQueryClient();
    const [showShareModal, setShowShareModal] = useState(false);

    const strategyContextFromQuery = useMemo(() => parseStrategyContextFromQuery(searchParams), [searchParams]);

    const accessSummaryQuery = useQuery({
        ...trpc.resourceAccess.getAccessSummary.queryOptions({
            resourcePk: scorecardId,
            resourceType: 'scorecard'
        }),
        enabled: scorecardId > 0
    });

    const scorecardQuery = useQuery({
        ...trpc.scorecards.getById.queryOptions({ scorecardId }),
        enabled: scorecardId > 0,
        refetchInterval: (query) => {
            const data = query.state.data;
            if (data?.scorecard.initializationStatus === 'extracting') return 3000;
            return data?.intendedStrategyId && data.scorecard.initializationStatus === 'pending' ? 3000 : false;
        }
    });

    const scorecard: EntryScorecardsRow | undefined = useMemo(() => scorecardQuery.data?.scorecard, [scorecardQuery.data]);
    const targetProvenance = scorecardQuery.data?.targetProvenance ?? null;

    const strategyContext = useMemo(() => {
        if (strategyContextFromQuery) {
            return strategyContextFromQuery;
        }

        if (!scorecard) {
            return null;
        }

        if (scorecardQuery.data?.intendedStrategyId) {
            return { strategyId: scorecardQuery.data.intendedStrategyId };
        }

        return scorecard.trainingStrategyId ? { strategyId: scorecard.trainingStrategyId } : null;
    }, [scorecard, scorecardQuery.data?.intendedStrategyId, strategyContextFromQuery]);

    const isOwnedScorecard = accessSummaryQuery.data?.ownedByUser === true;
    const canEditScorecard = isOwnedScorecard && scorecard?.lockedAt === null && scorecard.initializationStatus !== 'extracting';

    const [name, setName] = useState('');
    const [targetMetric, setTargetMetric] = useState<'stock' | 'option' | 'future'>('option');
    const [minPredictedProfit, setMinPredictedProfit] = useState<number>(5.0);
    const [thresholds, setThresholds] = useState<ThresholdsMeta>(defaultThresholds);
    const [sizingPolicy, setSizingPolicy] = useState<SizingBand[]>([]);
    const [vetoLiftThreshold, setVetoLiftThreshold] = useState<number | null>(null);
    const [baselineExperimentId, setBaselineExperimentId] = useState<number | null>(null);

    // Rules
    const [rules, setRules] = useState<ScorecardRuleRow[]>([]);

    const loadRules = useCallback(async () => {
        if (!scorecard) return;

        try {
            const r = await trpcClient.scorecards.listRules.query({ scorecardId: scorecard.id });
            setRules(r);
        } catch (e: unknown) {
            const err = e as Error;
            showToast(err?.message || 'Failed to load rules', 'error');
        }
    }, [scorecard]);

    useEffect(() => {
        void loadRules();
    }, [loadRules]);

    const ruleFeatureKeys = useMemo(() => {
        return Array.from(
            new Set(rules.map((rule) => rule.featureKey).filter((featureKey): featureKey is string => Boolean(featureKey)))
        ).sort((left, right) => left.localeCompare(right));
    }, [rules]);

    const registryFeaturesQuery = useQuery({
        ...trpc.scorecards.listScorecardFeatures.queryOptions({
            featureKeys: ruleFeatureKeys,
            includeUndiscoverable: true
        }),
        enabled: ruleFeatureKeys.length > 0,
        staleTime: 300000
    });

    const registryFeatureByKey = useMemo(() => {
        return new Map((registryFeaturesQuery.data ?? []).map((feature) => [feature.featureKey, feature]));
    }, [registryFeaturesQuery.data]);

    const getRegistryFeatureLabel = useCallback(
        (featureKey?: string | null): string | null => {
            return featureKey ? (registryFeatureByKey.get(featureKey)?.label ?? null) : null;
        },
        [registryFeatureByKey]
    );

    const getRuleFeatureLabel = useCallback(
        (rule: Pick<ScorecardRuleRow, 'featureKey' | 'display'>): string => {
            return getRegistryFeatureLabel(rule.featureKey) ?? rule.display.label;
        },
        [getRegistryFeatureLabel]
    );

    const getRuleSecondaryFeatureLabel = useCallback(
        (rule: Pick<ScorecardRuleRow, 'featureKey' | 'display'>): string | null => {
            const registryLabel = getRegistryFeatureLabel(rule.featureKey);

            if (!registryLabel || registryLabel === rule.display.label) return rule.display.secondaryLabel;

            return rule.display.label;
        },
        [getRegistryFeatureLabel]
    );

    // Delete scorecard
    const [confirmDeleteScorecardOpen, setConfirmDeleteScorecardOpen] = useState(false);
    const [deletingScorecard, setDeletingScorecard] = useState(false);

    useEffect(() => {
        if (!scorecard) return;
        setName(scorecard.name);
        setTargetMetric(scorecard.targetMetric);
        setMinPredictedProfit(Number(scorecard.minPredictedProfit));
        setVetoLiftThreshold(scorecard.vetoLiftThreshold ? Number(scorecard.vetoLiftThreshold) : null);

        try {
            const t = scorecard.thresholds
                ? (JSON.parse(scorecard.thresholds) as { minMatchedRules?: number; minPositiveMatchedRules?: number })
                : defaultThresholds;

            setThresholds({
                minMatchedRules: Number.isFinite(t.minMatchedRules) ? (t.minMatchedRules ?? defaultThresholds.minMatchedRules) : 1,
                minPositiveMatchedRules: Number.isFinite(t.minPositiveMatchedRules)
                    ? (t.minPositiveMatchedRules ?? defaultThresholds.minPositiveMatchedRules)
                    : defaultThresholds.minPositiveMatchedRules
            });
        } catch {
            setThresholds(defaultThresholds);
        }

        try {
            const s = scorecard.sizingPolicy ? (JSON.parse(scorecard.sizingPolicy) as SizingBand[]) : [];
            setSizingPolicy(Array.isArray(s) ? s : []);
        } catch {
            setSizingPolicy([]);
        }

        // Initialize baselineExperimentId from scorecard
        try {
            const beid = scorecard.baselineExperimentId;
            setBaselineExperimentId(beid != null ? Number(beid) : null);
        } catch {
            setBaselineExperimentId(null);
        }
    }, [scorecard]);

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: trpc.scorecards.getById.queryOptions({ scorecardId }).queryKey
        });
        void queryClient.invalidateQueries({
            queryKey: trpc.scorecards.list.queryOptions().queryKey
        });
        if (strategyContext) {
            void queryClient.invalidateQueries({
                queryKey: trpc.backtest.getStrategyScorecardContext.queryOptions({ strategyId: strategyContext.strategyId }).queryKey
            });
        }
    }, [queryClient, scorecardId, strategyContext]);

    const updateMetaMutation = useTrpcMutation(trpc.scorecards.updateMeta.mutationOptions({ onSuccess: invalidateList }), {
        successMessage: 'Scorecard updated',
        fallbackErrorMessage: 'Failed to update scorecard'
    });

    const deleteScorecard = useCallback(async () => {
        if (!scorecard) return;

        try {
            setDeletingScorecard(true);

            await trpcClient.scorecards.delete.mutate({ scorecardId: scorecard.id });

            showToast('Scorecard deleted', 'success');

            invalidateList();

            window.location.href = `/user/scorecards?${buildStrategyContextQueryString(strategyContext)}`;
        } catch (err) {
            const error = err as Error;
            showToast(error?.message || 'Failed to delete scorecard', 'error');
        } finally {
            setDeletingScorecard(false);
        }
    }, [scorecard, strategyContext, invalidateList]);

    const onSave = async (): Promise<boolean> => {
        if (!scorecard) return false;

        if (!baselineExperimentId) {
            showToast('Baseline experiment is required', 'error');
            return false;
        }

        const payload: {
            scorecardId: number;
            name?: string;
            targetMetric?: 'stock' | 'option' | 'future';
            minPredictedProfit?: number;
            thresholds?: { minMatchedRules?: number; minPositiveMatchedRules?: number };
            sizingPolicy?: SizingBand[];
            vetoLiftThreshold?: number | null;
            baselineExperimentId: number;
        } = { scorecardId: scorecard.id, baselineExperimentId };

        if (name && name !== scorecard.name) payload.name = name;
        if (targetMetric && targetMetric !== scorecard.targetMetric) payload.targetMetric = targetMetric;
        if (minPredictedProfit !== Number(scorecard.minPredictedProfit)) payload.minPredictedProfit = minPredictedProfit;

        payload.thresholds = {
            minMatchedRules: thresholds.minMatchedRules,
            minPositiveMatchedRules: thresholds.minPositiveMatchedRules
        };

        if (scorecard.targetMetric !== 'future') payload.sizingPolicy = sizingPolicy;
        if (vetoLiftThreshold !== Number(scorecard.vetoLiftThreshold)) payload.vetoLiftThreshold = vetoLiftThreshold;

        const result = await updateMetaMutation.mutateAsync(payload);
        if (result.baselineReextractStarted) {
            showToast('Baseline source changed. Wait for preparation to finish before testing this version.', 'warning');
            return false;
        }

        return true;
    };

    return (
        <UserLayout title="Scorecard Editor - Project 314">
            {scorecardQuery.isLoading || accessSummaryQuery.isLoading ? (
                <Card className="p-4">Loading...</Card>
            ) : !scorecard ? (
                <Card className="p-4 text-red-400">Scorecard not found or you do not have access.</Card>
            ) : (
                <div className="space-y-6">
                    <ResourceAccessNotice access={{ canEdit: canEditScorecard, ownedByUser: isOwnedScorecard }} noun="scorecard" />

                    {isOwnedScorecard && scorecard.initializationStatus === 'ready' && scorecard.lockedAt === null && (
                        <Card className="border border-emerald-800 bg-emerald-950/20 p-4 text-sm text-emerald-100">
                            Baselines are ready. Tune the rules and thresholds, preview them against symbols, then choose Test This Version.
                            Testing locks this exact version and attaches it to the strategy.
                        </Card>
                    )}

                    {isOwnedScorecard && scorecard.lockedAt !== null && (
                        <Card className="border border-blue-800 bg-blue-950/20 p-4 text-sm text-blue-100">
                            This tested version is locked so its results stay reproducible. Choose New Editable Version to make further
                            changes.
                        </Card>
                    )}

                    <ScorecardMetadataCard
                        canEditScorecard={canEditScorecard}
                        invalidateList={invalidateList}
                        isDeletingScorecard={deletingScorecard}
                        isOwnedScorecard={isOwnedScorecard}
                        isSaving={updateMetaMutation.isPending}
                        minPredictedProfit={minPredictedProfit}
                        name={name}
                        strategyContext={strategyContext}
                        scorecard={scorecard}
                        setMinPredictedProfit={setMinPredictedProfit}
                        setName={setName}
                        setSizingPolicy={setSizingPolicy}
                        setTargetMetric={setTargetMetric}
                        setThresholds={setThresholds}
                        setVetoLiftThreshold={setVetoLiftThreshold}
                        sizingPolicy={sizingPolicy}
                        targetProvenance={targetProvenance}
                        targetMetric={targetMetric}
                        thresholds={thresholds}
                        vetoLiftThreshold={vetoLiftThreshold}
                        onRequestDelete={() => setConfirmDeleteScorecardOpen(true)}
                        onSave={onSave}
                        onShare={() => setShowShareModal(true)}
                    />

                    <fieldset className="space-y-6" disabled={!canEditScorecard}>
                        {isOwnedScorecard && (
                            <BaselineExperimentCard
                                baselineExperimentId={baselineExperimentId}
                                canEditScorecard={canEditScorecard}
                                isOwnedScorecard={isOwnedScorecard}
                                strategyContext={strategyContext}
                                setBaselineExperimentId={setBaselineExperimentId}
                            />
                        )}

                        {isOwnedScorecard && <LinkedExperimentCard isOwnedScorecard={isOwnedScorecard} scorecard={scorecard} />}

                        {canEditScorecard && (
                            <AddRuleCard canEditScorecard={canEditScorecard} loadRules={loadRules} scorecard={scorecard} />
                        )}

                        <RulesCard
                            getRuleFeatureLabel={getRuleFeatureLabel}
                            getRuleSecondaryFeatureLabel={getRuleSecondaryFeatureLabel}
                            loadRules={loadRules}
                            rules={rules}
                            setRules={setRules}
                        />
                    </fieldset>

                    <PreviewCard
                        minPredictedProfit={minPredictedProfit}
                        registryFeatureByKey={registryFeatureByKey}
                        scorecard={scorecard}
                        strategyContext={strategyContext}
                        thresholds={thresholds}
                    />

                    {showShareModal && (
                        <ShareResourceModal
                            isOpen={showShareModal}
                            resourceLabel={scorecard.name}
                            resourcePk={scorecard.id}
                            resourceType="scorecard"
                            onClose={() => setShowShareModal(false)}
                        />
                    )}

                    {/* Delete scorecard confirmation */}
                    <ConfirmationDialog
                        confirmText={deletingScorecard ? 'Deleting…' : 'Delete'}
                        confirmVariant="danger"
                        isOpen={confirmDeleteScorecardOpen}
                        message={
                            <div className="space-y-2">
                                <p>
                                    This will permanently delete the scorecard and all its rules. Attached scorecards must be detached
                                    first.
                                </p>
                                {scorecard.initializationStatus === 'extracting' && (
                                    <p className="text-yellow-500 text-sm">
                                        Baseline extraction is in progress. Consider waiting until it completes.
                                    </p>
                                )}
                            </div>
                        }
                        title="Delete Scorecard"
                        onCancel={() => {
                            if (deletingScorecard) return;
                            setConfirmDeleteScorecardOpen(false);
                        }}
                        onConfirm={async () => {
                            if (deletingScorecard) return;
                            await deleteScorecard();
                            setConfirmDeleteScorecardOpen(false);
                        }}
                    />
                </div>
            )}
        </UserLayout>
    );
};

export default ScorecardEditor;
